import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';
import EditProjectModal from './EditProjectModal';
import Image from '../../../components/AppImage';
import { projectService } from '../../../services/projectService';
import AssignContractorModal from './AssignContractorModal';
import TeamMemberSelector from '../../../components/ui/TeamMemberSelector';
import DocumentUpload from '../../permits/components/DocumentUpload';
import { useBottomSheet } from '../../../hooks/useBottomSheet';

const PHASES = [
  { id: 'planning', label: 'Planning', icon: 'ClipboardList' },
  { id: 'permits', label: 'Permits', icon: 'FileCheck' },
  { id: 'foundation', label: 'Foundation', icon: 'Layers' },
  { id: 'framing', label: 'Framing', icon: 'Box' },
  { id: 'systems', label: 'Systems', icon: 'Zap' },
  { id: 'finishing', label: 'Finishing', icon: 'Paintbrush' },
  { id: 'completed', label: 'Completed', icon: 'CheckCircle2' }
];

const ProjectDetails = ({ project, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showTeamMemberModal, setShowTeamMemberModal] = useState(false);
  const [assignedContractors, setAssignedContractors] = useState([]);
  const [assignedTeamMembers, setAssignedTeamMembers] = useState([]);
  const [projectPermits, setProjectPermits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [unassigningContractor, setUnassigningContractor] = useState(null);
  const [unassigningTeamMember, setUnassigningTeamMember] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (project?.id) {
      loadProjectRelations();
    }
  }, [project?.id]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const loadProjectRelations = async () => {
    setLoading(true);
    try {
      const [contractors, permits, teamMembers] = await Promise.all([
        projectService?.getAssignedContractors(project?.id),
        projectService?.getProjectPermits(project?.id),
        projectService?.getAssignedTeamMembers(project?.id)
      ]);
      setAssignedContractors(contractors || []);
      setProjectPermits(permits || []);
      setAssignedTeamMembers(teamMembers || []);
    } catch (error) {
      console.error('Error loading project relations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToContractor = (contractorId) => {
    const event = new CustomEvent('openContractorProfile', { detail: { contractorId } });
    window.dispatchEvent(event);
    onClose?.();
  };

  const handleNavigateToPermit = (permitId) => {
    const event = new CustomEvent('openPermitDetails', { detail: { permitId } });
    window.dispatchEvent(event);
    onClose?.();
  };

  const handleUnassignContractor = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to unassign this contractor?')) return;
    setUnassigningContractor(assignmentId);
    try {
      await projectService?.unassignContractor(assignmentId);
      await loadProjectRelations();
    } catch (error) {
      console.error('Error unassigning contractor:', error);
      alert('Failed to unassign contractor');
    } finally {
      setUnassigningContractor(null);
    }
  };

  const handleAssignSuccess = async () => {
    await loadProjectRelations();
    setShowAssignModal(false);
  };

  const handleTeamMemberSelect = async (member) => {
    try {
      await projectService?.assignTeamMember(project?.id, member?.id, {
        role: 'Team Member',
        status: 'assigned'
      });
      await loadProjectRelations();
    } catch (error) {
      console.error('Error assigning team member:', error);
      alert('Failed to assign team member');
    }
  };

  const handleUnassignTeamMember = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to unassign this team member?')) return;
    setUnassigningTeamMember(assignmentId);
    try {
      await projectService?.unassignTeamMember(assignmentId);
      await loadProjectRelations();
    } catch (error) {
      console.error('Error unassigning team member:', error);
      alert('Failed to unassign team member');
    } finally {
      setUnassigningTeamMember(null);
    }
  };

  const handleNavigateToTeamMember = () => {
    const event = new CustomEvent('navigateToSettings', { detail: { tab: 'team' } });
    window.dispatchEvent(event);
    onClose?.();
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) return;
    setDeleting(true);
    try {
      await projectService?.delete(project?.id);
      onUpdate?.();
      onClose?.();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project: ' + (error?.message || 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    onUpdate?.();
  };

  const getPhaseColor = (phase) => {
    const colors = {
      'planning': 'bg-blue-50 text-blue-700 border-blue-200',
      'permits': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'foundation': 'bg-orange-50 text-orange-700 border-orange-200',
      'framing': 'bg-purple-50 text-purple-700 border-purple-200',
      'systems': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'finishing': 'bg-pink-50 text-pink-700 border-pink-200',
      'completed': 'bg-green-50 text-green-700 border-green-200'
    };
    return colors?.[phase] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getPhaseIcon = (phase) => {
    const icons = {
      'planning': 'ClipboardList',
      'permits': 'FileCheck',
      'foundation': 'Layers',
      'framing': 'Box',
      'systems': 'Zap',
      'finishing': 'Paintbrush',
      'completed': 'CheckCircle2'
    };
    return icons?.[phase] || 'Circle';
  };

  const getProgressPercent = () => {
    const phaseIndex = PHASES?.findIndex(p => p?.id === project?.phase);
    if (phaseIndex < 0) return 0;
    return Math.round(((phaseIndex + 1) / PHASES?.length) * 100);
  };

  const tabs = [
    { id: 'timeline', label: 'Timeline', icon: 'Calendar' },
    { id: 'permits', label: 'Permits', icon: 'FileCheck' },
    { id: 'contractors', label: 'Contractors', icon: 'Users' },
    { id: 'team', label: 'Team', icon: 'UserCheck' },
    { id: 'budget', label: 'Budget', icon: 'DollarSign' },
    { id: 'documents', label: 'Docs', icon: 'FileText' }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return '$' + Number(amount)?.toLocaleString();
  };

  const getStatusColor = (status) => {
    const colors = {
      'assigned': 'bg-blue-100 text-blue-700',
      'in_progress': 'bg-warning/10 text-warning',
      'completed': 'bg-success/10 text-success',
      'on_hold': 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-700';
  };

  const renderTimeline = () => {
    const currentPhaseIndex = PHASES?.findIndex(p => p?.id === project?.phase);
    return (
      <div className="space-y-6">
        <div className="bg-muted/50 rounded-xl p-4 sm:p-5 border border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-foreground">Overall Progress</p>
            <p className="text-sm font-bold text-primary">{getProgressPercent()}%</p>
          </div>
          <div className="w-full bg-border rounded-full h-3">
            <div
              className="bg-primary rounded-full h-3 transition-all duration-500"
              style={{ width: `${getProgressPercent()}%` }}
            />
          </div>
          {project?.dueDate && (
            <p className="text-xs text-muted-foreground mt-2">
              Due: {formatDate(project?.dueDate)}
            </p>
          )}
        </div>

        <div className="space-y-4">
          {PHASES?.map((phase, index) => {
            const isCompleted = index < currentPhaseIndex;
            const isCurrent = index === currentPhaseIndex;
            return (
              <div key={phase?.id} className="flex gap-3 sm:gap-4">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={cn(
                    'w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center',
                    isCompleted ? 'bg-success' : isCurrent ? 'bg-primary' : 'bg-muted'
                  )}>
                    <Icon
                      name={isCompleted ? 'Check' : phase?.icon}
                      size={16}
                      color={isCompleted || isCurrent ? '#FFFFFF' : 'var(--color-muted-foreground)'}
                    />
                  </div>
                  {index < PHASES?.length - 1 && (
                    <div className={cn('w-0.5 h-10', isCompleted ? 'bg-success' : 'bg-border')} />
                  )}
                </div>
                <div className="flex-1 pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={cn(
                      'font-semibold text-sm sm:text-base',
                      isCurrent ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'
                    )}>
                      {phase?.label}
                    </h4>
                    {isCurrent && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        Current
                      </span>
                    )}
                    {isCompleted && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                        Done
                      </span>
                    )}
                  </div>
                  {isCurrent && project?.nextMilestone && (
                    <p className="text-sm text-muted-foreground">Next: {project?.nextMilestone}</p>
                  )}
                  {isCurrent && project?.phaseDuration && (
                    <p className="text-xs text-muted-foreground mt-1">Duration: {project?.phaseDuration} days</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPermits = () => (
    <div className="space-y-4">
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm">Loading permits...</p>
        </div>
      ) : projectPermits?.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="FileCheck" size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm font-medium">No permits found for this project</p>
        </div>
      ) : (
        projectPermits?.map((permit) => (
          <button
            key={permit?.id}
            onClick={() => handleNavigateToPermit(permit?.id)}
            className="w-full bg-muted/50 rounded-xl p-4 hover:bg-muted hover:shadow-md transition-all text-left border border-transparent hover:border-border"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm sm:text-base text-foreground mb-1 truncate">
                  {permit?.projectName}
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  #{permit?.applicationNumber}
                </p>
              </div>
              <span className={`flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-medium ${
                permit?.status === 'approved' ? 'bg-success/10 text-success' :
                permit?.status === 'under review' ? 'bg-warning/10 text-warning' :
                permit?.status === 'submission' ? 'bg-blue-100 text-blue-700' : 'bg-destructive/10 text-destructive'
              }`}>
                {permit?.status?.charAt(0)?.toUpperCase() + permit?.status?.slice(1)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Submitted</p>
                <p className="text-sm font-medium text-foreground">{formatDate(permit?.submittedDate)}</p>
              </div>
              {permit?.deadline && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Deadline</p>
                  <p className="text-sm font-medium text-foreground">{formatDate(permit?.deadline)}</p>
                </div>
              )}
            </div>
          </button>
        ))
      )}
    </div>
  );

  const renderContractors = () => (
    <div className="space-y-4">
      {/* Sticky action button at bottom on mobile */}
      <div className="flex justify-end mb-4">
        <Button
          onClick={() => setShowAssignModal(true)}
          leftIcon={<Icon name="Plus" size={16} />}
          className="h-11 px-4 text-sm"
        >
          Assign Contractor
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm">Loading contractors...</p>
        </div>
      ) : assignedContractors?.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="Users" size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm font-medium">No contractors assigned</p>
          <p className="text-xs mt-1">Tap "Assign Contractor" to add one</p>
        </div>
      ) : (
        assignedContractors?.map((assignment) => (
          <div key={assignment?.id} className="bg-muted/50 rounded-xl p-4 border border-border">
            <div className="flex items-start gap-3 mb-4">
              <button
                onClick={() => handleNavigateToContractor(assignment?.contractors?.id)}
                className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity text-left min-w-0"
              >
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  {assignment?.contractors?.avatar ? (
                    <Image
                      src={assignment?.contractors?.avatar}
                      alt={assignment?.contractors?.name || 'Contractor'}
                      className="w-11 h-11 rounded-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={22} className="text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm text-foreground mb-0.5 truncate">
                    {assignment?.contractors?.name}
                  </h4>
                  <p className="text-xs text-muted-foreground truncate">
                    {assignment?.role || assignment?.contractors?.role || 'Contractor'}
                  </p>
                </div>
              </button>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(assignment?.status)}`}>
                  {assignment?.status?.replace('_', ' ')?.charAt(0)?.toUpperCase() + assignment?.status?.slice(1)?.replace('_', ' ')}
                </span>
                <button
                  onClick={() => handleUnassignContractor(assignment?.id)}
                  disabled={unassigningContractor === assignment?.id}
                  className="p-2 hover:bg-destructive/10 rounded-lg text-destructive transition-colors disabled:opacity-50 min-w-[36px] min-h-[36px] flex items-center justify-center"
                  title="Unassign contractor"
                >
                  {unassigningContractor === assignment?.id ? (
                    <Icon name="Loader2" size={16} className="animate-spin" />
                  ) : (
                    <Icon name="X" size={16} />
                  )}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Start Date</p>
                <p className="font-medium text-sm text-foreground">{formatDate(assignment?.startDate)}</p>
              </div>
              {assignment?.endDate && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">End Date</p>
                  <p className="font-medium text-sm text-foreground">{formatDate(assignment?.endDate)}</p>
                </div>
              )}
            </div>
            {assignment?.notes && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{assignment?.notes}</p>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderTeamMembers = () => (
    <div className="space-y-4">
      <div className="flex justify-end mb-4">
        <Button
          onClick={() => setShowTeamMemberModal(true)}
          leftIcon={<Icon name="Plus" size={16} />}
          className="h-11 px-4 text-sm"
        >
          Assign Team Member
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm">Loading team members...</p>
        </div>
      ) : assignedTeamMembers?.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="UserCheck" size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm font-medium">No team members assigned</p>
          <p className="text-xs mt-1">Tap "Assign Team Member" to add one</p>
        </div>
      ) : (
        assignedTeamMembers?.map((assignment) => (
          <div key={assignment?.id} className="bg-muted/50 rounded-xl p-4 border border-border">
            <div className="flex items-start gap-3 mb-4">
              <button
                onClick={handleNavigateToTeamMember}
                className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity text-left min-w-0"
              >
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  {assignment?.userProfiles?.avatarUrl ? (
                    <img
                      src={assignment?.userProfiles?.avatarUrl}
                      alt={assignment?.userProfiles?.fullName || 'Team member'}
                      className="w-11 h-11 rounded-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={22} className="text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm text-foreground mb-0.5 truncate">
                    {assignment?.userProfiles?.fullName || 'Team Member'}
                  </h4>
                  <p className="text-xs text-muted-foreground truncate">{assignment?.role}</p>
                  <p className="text-xs text-muted-foreground truncate">{assignment?.userProfiles?.email}</p>
                </div>
              </button>
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                  assignment?.status === 'assigned' ? 'bg-blue-100 text-blue-700' :
                  assignment?.status === 'in_progress' ? 'bg-warning/10 text-warning' :
                  assignment?.status === 'completed' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                }`}>
                  {assignment?.status?.replace('_', ' ')?.charAt(0)?.toUpperCase() + assignment?.status?.slice(1)?.replace('_', ' ')}
                </span>
                <button
                  onClick={() => handleUnassignTeamMember(assignment?.id)}
                  disabled={unassigningTeamMember === assignment?.id}
                  className="text-destructive hover:text-destructive/80 text-xs flex items-center gap-1 font-medium disabled:opacity-50 min-h-[36px] px-2"
                >
                  {unassigningTeamMember === assignment?.id ? (
                    <Icon name="Loader2" size={12} className="animate-spin" />
                  ) : (
                    <Icon name="X" size={12} />
                  )}
                  Unassign
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Start Date</p>
                <p className="font-medium text-sm text-foreground">{formatDate(assignment?.startDate)}</p>
              </div>
              {assignment?.endDate && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">End Date</p>
                  <p className="font-medium text-sm text-foreground">{formatDate(assignment?.endDate)}</p>
                </div>
              )}
            </div>
            {assignment?.notes && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{assignment?.notes}</p>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderBudget = () => {
    const total = Number(project?.budgetTotal) || 0;
    const spent = Number(project?.budgetSpent) || 0;
    const remaining = total - spent;
    const spentPercent = total > 0 ? Math.min(Math.round((spent / total) * 100), 100) : 0;

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5">
          <div className="bg-muted/50 rounded-xl p-4 sm:p-6 border border-border">
            <p className="text-xs sm:text-sm text-muted-foreground mb-1.5">Total Budget</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">{formatCurrency(total)}</p>
          </div>
          <div className="bg-muted/50 rounded-xl p-4 sm:p-6 border border-border">
            <p className="text-xs sm:text-sm text-muted-foreground mb-1.5">Spent</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-warning">{formatCurrency(spent)}</p>
          </div>
          <div className="bg-muted/50 rounded-xl p-4 sm:p-6 border border-border">
            <p className="text-xs sm:text-sm text-muted-foreground mb-1.5">Remaining</p>
            <p className={cn('text-xl sm:text-2xl md:text-3xl font-bold', remaining >= 0 ? 'text-success' : 'text-destructive')}>
              {formatCurrency(remaining)}
            </p>
          </div>
        </div>

        {total > 0 && (
          <div className="bg-muted/50 rounded-xl p-4 sm:p-6 border border-border">
            <h4 className="font-semibold text-sm sm:text-base text-foreground mb-3">Budget Utilization</h4>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Spent vs Total</span>
              <span className="text-sm font-semibold text-foreground">{spentPercent}%</span>
            </div>
            <div className="w-full bg-border rounded-full h-3">
              <div
                className={cn('rounded-full h-3 transition-all duration-500', spentPercent > 90 ? 'bg-destructive' : spentPercent > 70 ? 'bg-warning' : 'bg-success')}
                style={{ width: `${spentPercent}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {formatCurrency(spent)} spent of {formatCurrency(total)} total budget
            </p>
          </div>
        )}

        {total === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="DollarSign" size={48} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium">No budget set for this project</p>
            <p className="text-xs mt-1">Edit the project to add budget information</p>
          </div>
        )}
      </div>
    );
  };

  const renderDocuments = () => (
    <div className="space-y-4">
      <p className="text-xs sm:text-sm text-muted-foreground">
        Upload and manage documents for the permits linked to this project.
      </p>
      {projectPermits?.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="FileText" size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm font-medium">No permits linked to this project</p>
          <p className="text-xs mt-1">Add a permit first to upload documents</p>
        </div>
      ) : (
        <div className="space-y-4">
          {projectPermits?.map((permit) => (
            <div key={permit?.id} className="border border-border rounded-xl p-4">
              <h4 className="font-semibold text-xs sm:text-sm text-foreground mb-3 flex items-center gap-2">
                <Icon name="FileCheck" size={14} className="text-primary flex-shrink-0" />
                <span className="truncate">{permit?.projectName || permit?.permitType} — #{permit?.applicationNumber}</span>
              </h4>
              <DocumentUpload permitId={permit?.id} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const progressPercent = getProgressPercent();

  // ── Swipe gesture + scroll shadows for mobile bottom sheet ──
  const {
    sheetStyle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    scrollRef: mobileScrollRef,
    showTopShadow: mobileTopShadow,
    showBottomShadow: mobileBottomShadow,
  } = useBottomSheet(onClose);

  // ── Mobile: Bottom Sheet Layout ──
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 z-[399] bg-black/60 backdrop-blur-sm"
          onClick={onClose}
          aria-label="Close panel"
        />

        {/* Bottom Sheet */}
        <div
          className="fixed bottom-0 left-0 right-0 z-[400] bg-card rounded-t-2xl shadow-2xl flex flex-col"
          style={{
            maxHeight: 'calc(92vh - 64px)',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
            ...sheetStyle
          }}
        >
          {/* Enhanced drag handle */}
          <div
            className="flex-shrink-0 flex flex-col items-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none select-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
            <span className="text-[10px] text-muted-foreground/40 mt-1 font-medium tracking-wide">DRAG TO CLOSE</span>
          </div>

          {/* Header */}
          <div className="flex-shrink-0 bg-card border-b border-border">
            {/* Title row */}
            <div className="flex items-center gap-2 px-4 py-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-base text-foreground truncate leading-tight">
                  {project?.customerName}
                </h3>
                <p className="text-xs text-muted-foreground truncate">{project?.address}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex items-center gap-1 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-semibold hover:bg-primary/90 transition-colors min-h-[40px]"
                >
                  <Icon name="Edit" size={14} />
                  <span>Edit</span>
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center disabled:opacity-50"
                >
                  {deleting ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="Trash2" size={15} />}
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center border border-border"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>

            {/* Phase + progress */}
            <div className="px-4 pb-2">
              <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                <span className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border',
                  getPhaseColor(project?.phase)
                )}>
                  <Icon name={getPhaseIcon(project?.phase)} size={11} />
                  {project?.phase?.charAt(0)?.toUpperCase() + project?.phase?.slice(1)}
                </span>
                {project?.dueDate && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                    <Icon name="Calendar" size={10} />
                    Due {formatDate(project?.dueDate)}
                  </span>
                )}
                <span className="ml-auto text-xs font-semibold text-primary">{progressPercent}%</span>
              </div>
              <div className="w-full bg-border rounded-full h-1.5">
                <div
                  className="bg-primary rounded-full h-1.5 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Tabs - horizontally scrollable */}
            <div className="tabs-scroll-container px-4 pb-2 gap-1">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 min-h-[36px] min-w-[60px] justify-center ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={12} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Scroll indicator - top */}
          <div
            className={`flex-shrink-0 h-5 pointer-events-none transition-opacity duration-200 ${mobileTopShadow ? 'opacity-100' : 'opacity-0'}`}
            style={{ background: 'linear-gradient(to bottom, hsl(var(--card)) 0%, transparent 100%)', marginBottom: '-20px', position: 'relative', zIndex: 2 }}
          />

          {/* Scrollable content */}
          <div ref={mobileScrollRef} className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
            {activeTab === 'timeline' && renderTimeline()}
            {activeTab === 'permits' && renderPermits()}
            {activeTab === 'contractors' && renderContractors()}
            {activeTab === 'team' && renderTeamMembers()}
            {activeTab === 'budget' && renderBudget()}
            {activeTab === 'documents' && renderDocuments()}
          </div>

          {/* Scroll indicator - bottom */}
          <div
            className={`flex-shrink-0 h-5 pointer-events-none transition-opacity duration-200 ${mobileBottomShadow ? 'opacity-100' : 'opacity-0'}`}
            style={{ background: 'linear-gradient(to top, hsl(var(--card)) 0%, transparent 100%)', marginTop: '-20px', position: 'relative', zIndex: 2 }}
          />
        </div>

        {showEditModal && (
          <EditProjectModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSuccess={handleEditSuccess}
            project={project}
          />
        )}
        {showAssignModal && (
          <AssignContractorModal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            onSuccess={handleAssignSuccess}
            projectId={project?.id}
          />
        )}
        {showTeamMemberModal && (
          <TeamMemberSelector
            isOpen={showTeamMemberModal}
            onClose={() => setShowTeamMemberModal(false)}
            onSelect={handleTeamMemberSelect}
            title="Assign Team Member to Project"
            showAvailability={true}
          />
        )}
      </>
    );
  }

  // ── Desktop: Centered Modal Layout ──
  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 z-[199] bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close panel"
      />

      {/* Centered Modal */}
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 pointer-events-none">
        <div className="pointer-events-auto w-full max-w-[85vw] h-[90vh] bg-card shadow-2xl flex flex-col rounded-2xl overflow-hidden">

          {/* Sticky Top Bar */}
          <div className="flex-shrink-0 bg-card border-b border-border">
            {/* Row 1: Title + Actions */}
            <div className="flex items-center gap-2 px-5 py-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg sm:text-xl text-foreground truncate leading-tight">
                  {project?.customerName}
                </h3>
                <p className="text-sm text-muted-foreground truncate">{project?.address}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors min-h-[38px] whitespace-nowrap"
                >
                  <Icon name="Edit" size={15} />
                  <span>Edit</span>
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors min-h-[38px] min-w-[38px] flex items-center justify-center disabled:opacity-50"
                >
                  {deleting ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Trash2" size={16} />}
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors min-h-[38px] min-w-[38px] flex items-center justify-center border border-border"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            {/* Row 2: Phase + progress */}
            <div className="px-5 pb-3">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border',
                  getPhaseColor(project?.phase)
                )}>
                  <Icon name={getPhaseIcon(project?.phase)} size={13} />
                  {project?.phase?.charAt(0)?.toUpperCase() + project?.phase?.slice(1)}
                </span>
                {project?.dueDate && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                    <Icon name="Calendar" size={12} />
                    Due {formatDate(project?.dueDate)}
                  </span>
                )}
                <span className="ml-auto text-xs font-semibold text-primary">{progressPercent}%</span>
              </div>
              <div className="w-full bg-border rounded-full h-1.5">
                <div
                  className="bg-primary rounded-full h-1.5 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Row 3: Tab navigation */}
            <div className="flex gap-1 overflow-x-auto px-5 pb-3 scrollbar-none" style={{ scrollbarWidth: 'none' }}>
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap flex-shrink-0 min-h-[34px] ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={13} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-5 sm:p-6">
            {activeTab === 'timeline' && renderTimeline()}
            {activeTab === 'permits' && renderPermits()}
            {activeTab === 'contractors' && renderContractors()}
            {activeTab === 'team' && renderTeamMembers()}
            {activeTab === 'budget' && renderBudget()}
            {activeTab === 'documents' && renderDocuments()}
          </div>
        </div>
      </div>

      {showEditModal && (
        <EditProjectModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
          project={project}
        />
      )}
      {showAssignModal && (
        <AssignContractorModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onSuccess={handleAssignSuccess}
          projectId={project?.id}
        />
      )}
      {showTeamMemberModal && (
        <TeamMemberSelector
          isOpen={showTeamMemberModal}
          onClose={() => setShowTeamMemberModal(false)}
          onSelect={handleTeamMemberSelect}
          title="Assign Team Member to Project"
          showAvailability={true}
        />
      )}
    </>
  );
};

export default ProjectDetails;